import clsx from 'clsx';
import { useState } from 'react';
import { Api, Parameter } from '../api';
import { FilterIcon } from '../icons/filter';
import { HistoryIcon } from '../icons/history';
import { TH } from '../shared/table';
import { COLS_FULL } from './columns';
import { Search } from '../shared/search';
import { ParameterRow } from './parameter-row';
import { DateInput } from '../shared/date-input';
import { DownloadDataIcon } from '../icons/download-data';

type Keys = keyof Parameter;

export const HistoricData = () => {
  const parameters = Api.list_historic_data();
  const [from, setFrom] = useState<number>(0);
  const [to, setTo] = useState<number>(Date.now());
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [search, setSearch] = useState<Record<Keys, string>>();
  const [sorting, setSorting] = useState<{
    key: Keys;
    asc: boolean;
  }>({
    key: 'date',
    asc: true,
  });

  const filteredParameters = parameters
    .filter((parameter) =>
      Object.values(parameter)
        .filter((v) => typeof v === 'string')
        .some((v) =>
          String(v).toLowerCase().includes(globalSearch.toLowerCase()),
        ),
    )
    .filter((parameter) =>
      Object.entries(search || {}).every(([key, value]) =>
        // @ts-ignore
        parameter[key].toString().toLowerCase().includes(value.toLowerCase()),
      ),
    )
    .sort((a, b) => {
      const ak = String(a[sorting.key]);
      const bk = String(b[sorting.key]);
      return sorting.asc
        ? ak.localeCompare(bk, 'en', { numeric: true })
        : bk.localeCompare(ak, 'en', { numeric: true });
    });

  const activeFiltersCount =
    Object.values(search || {}).filter((v) => v.length > 0).length +
    (globalSearch.length > 0 ? 1 : 0);

  return (
    <div className="h-full flex flex-col px-8 py-4 gap-4">
      <div className="h-[50px] flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <HistoryIcon className="w-8 h-8 fill-current" />
          Storico Dati
        </h1>
        <div className={'flex items-center gap-4'}>
          <div className="flex items-center">
            <span className="text-neutral-400 mr-2">Da</span>
            <DateInput
              value={from}
              onChange={(v) => setFrom(Number(v))}
              compact
            />
          </div>
          <div className="flex items-center">
            <span className="text-neutral-400 mr-2">A</span>
            <DateInput value={to} onChange={(v) => setTo(Number(v))} compact />
          </div>
          <div
            onClick={() => {
              setSearch(undefined);
              setGlobalSearch('');
            }}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-bold flex items-center gap-2',
              activeFiltersCount > 0
                ? 'cursor-pointer bg-orange-400 text-white'
                : 'bg-neutral-300 text-neutral-700',
            )}>
            <FilterIcon className="w-5 h-5" active={activeFiltersCount > 0} />{' '}
            Cancella filtri ({activeFiltersCount})
          </div>
          <Search value={globalSearch} onSearch={setGlobalSearch} />
        </div>
      </div>
      <div
        className="overflow-y-auto pr-2"
        style={{ height: 'calc(100vh - 110px)' }}>
        <table className="w-full">
          <thead>
            <tr>
              {COLS_FULL.map((col, i) => (
                <TH
                  key={i}
                  onClick={() => {
                    if (col.sortable) {
                      setSorting({
                        key: col.key,
                        asc: sorting.key === col.key ? !sorting.asc : true,
                      });
                    }
                  }}>
                  <div className="flex items-center justify-between mb-1 cursor-pointer">
                    {col.label}
                    {col.sortable && (
                      <button className="text-xs text-gray-500 flex flex-col gap-0">
                        <span
                          className={clsx(
                            'inline-block leading-[0.5rem] self-end',
                            sorting.key === col.key && sorting.asc
                              ? 'text-black'
                              : 'text-neutral-400',
                          )}>
                          ▲
                        </span>
                        <span
                          className={clsx(
                            'inline-block leading-[0.5rem] self-start',
                            sorting.key === col.key && !sorting.asc
                              ? 'text-black'
                              : 'text-neutral-400',
                          )}>
                          ▼
                        </span>
                      </button>
                    )}
                  </div>

                  {col.searchable && (
                    <div className="relative w-full h-6 bg-red-300">
                      <div className="absolute w-full bg-green-100">
                        <Search
                          size="small"
                          value={search?.[col.key] || ''}
                          onSearch={(v) =>
                            setSearch({
                              ...(search || {}),
                              [col.key]: v,
                            } as Record<Keys, string>)
                          }
                        />
                      </div>
                    </div>
                  )}
                </TH>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredParameters.map((par, i) => (
              <ParameterRow key={i} parameter={par} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="h-[60px] flex justify-end gap-4 my-4">
        <div
          className="cursor-pointer hover:scale-105 transition-all flex items-center gap-4 p-4 text-white bg-green-500 w-64 rounded-xl font-bold"
          onClick={() => {
            const csv = [
              Object.keys(COLS_FULL)
                .map((col) => COLS_FULL[col as any].label)
                .join(','),
              ...filteredParameters.map((par) =>
                COLS_FULL.map((col) => par[col.key]).join(','),
              ),
            ].join('\n');

            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'historic-data.csv';
            a.click();
            URL.revokeObjectURL(url);
          }}>
          <DownloadDataIcon className="w-6 h-6 fill-white" />
          Scarica dati in .csv
        </div>
      </div>
    </div>
  );
};
